import { useState, useRef, useEffect, useCallback } from "react";
import * as acorn from "acorn";
import {
    Box,
  VStack,
  HStack,
  Text,
  Button,
  Heading,
  Textarea,
} from "@chakra-ui/react";
import { useColorModeValue } from "./ui/color-mode";

/**
 * AccessibleCodeEditor - Voice-First Code Editor
 * Designed specifically for blind users with:
 * - Natural Language Voice Coding (Dictation -> Code)
 * - Clear ARIA labels and live regions
 * - Audio feedback & TTS
 */
export const AccessibleCodeEditor = ({
  value,
  onChange,
  onRun,
  output,
  error,
  hint,
  ariaLabel,
  question,
  description,
}) => {
  const [lineCount, setLineCount] = useState(1);
  const [currentLine, setCurrentLine] = useState(1);
  const [isListening, setIsListening] = useState(false);
  const [isExplaining, setIsExplaining] = useState(false);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);
  const latestValueRef = useRef(value);

  // Keep the ref updated with the latest prop to avoid stale closures in event listeners
  useEffect(() => {
    latestValueRef.current = value;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.300", "gray.600");
  const textColor = useColorModeValue("gray.800", "gray.100");
  const outputBg = useColorModeValue("gray.50", "gray.900");
  const errorColor = useColorModeValue("red.600", "red.300");
  const successColor = useColorModeValue("green.600", "green.300");

  useEffect(() => {
    const safeValue = typeof value === "string" ? value : "";
    const lines = safeValue.split("\n").length;
    setLineCount(lines);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    // Initialize Speech Recognition Native API
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = "en-US";

      recognitionRef.current.onstart = () => {
        setIsListening(true);
        announceToScreenReader("Listening for code dictation...");
        speakText("Listening");
      };

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        processVoiceCommand(transcript);
      };

      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error", event.error);
        setIsListening(false);
        speakText("Sorry, I didn't catch that.");
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Text to Speech Function (The Co-Pilot's voice)
  const speakText = useCallback((text) => {
    if ("speechSynthesis" in window) {
      // Cancel any ongoing speech so it's snappy
      window.speechSynthesis.cancel();
      const msg = new SpeechSynthesisUtterance(text);
      msg.rate = 1.1; // slightly faster so it feels responsive
      window.speechSynthesis.speak(msg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const handleClearCode = () => {
    onChange("");
    announceToScreenReader("Code cleared");
    speakText("Code cleared");
    textareaRef.current?.focus();
  };

  const processVoiceCommand = useCallback(
    (transcript) => {
      let lower = transcript.toLowerCase().trim();
      // Remove trailing punctuation
      lower = lower.replace(/[.,!?]$/, "");
      let codeToAdd = "";
      let spokenResponse = "";

      // Function Declaration
      if (lower.startsWith("create a function called")) {
        const namePart = lower.replace("create a function called", "").trim();
        const functionName = namePart.split(" ")[0] || "myFunction";
        const cleanName = functionName.replace(/[^a-zA-Z0-9]/g, "");
        codeToAdd = `\nfunction ${cleanName}() {\n  // write logic here\n}\n`;
        spokenResponse = `Created function ${cleanName}.`;

        // Variable Assignment ("name is equal to b", "create a LED variable with value ABC", "let x equal y")
      } else if (
        !lower.startsWith("if ") &&
        !lower.startsWith("while ") &&
        !lower.startsWith("for ") &&
        lower.match(
          /(?:is equal to|equal to|equals|equal|=|with value|and set it to)/,
        )
      ) {
        let match = lower.match(
          /^(?:can you )?(?:let|set|create a(?:n)? (?:let|led|new)?\s*variable(?: called| named)?\s*)?(.*?)\s+(?:is equal to|equal to|equals|equal|to|=|with value|and set it to)\s+(.+)$/,
        );

        if (match) {
          let varName = match[1].trim().replace(/[^a-zA-Z0-9]/g, "");
          if (!varName) varName = "myVar"; // fallback if they just said "create a let variable with value ABC"

          let val = match[2].trim();
          const origMatch = transcript.match(
            new RegExp(
              `(?:is equal to|equal to|equals|equal|=|with value|and set it to)\\s+(.+)$`,
              "i",
            ),
          );
          if (origMatch) val = origMatch[1].replace(/[.,!?]$/, "").trim();

          if (!isNaN(val)) {
            // number
          } else if (
            val.toLowerCase() === "true" ||
            val.toLowerCase() === "false"
          ) {
            val = val.toLowerCase();
          } else {
            val = `"${val}"`;
          }
          codeToAdd = `\nlet ${varName} = ${val};\n`;
          spokenResponse = `Created variable ${varName}.`;
        } else {
          // If it matched the keyword but not the full pattern, fallback to basic declare
          const fallbackName =
            lower
              .replace(/^(?:create a variable called|let|set)\s+/, "")
              .split(" ")[0]
              .replace(/[^a-zA-Z0-9]/g, "") || "myVar";
          codeToAdd = `\nlet ${fallbackName};\n`;
          spokenResponse = `Created variable ${fallbackName}.`;
        }

        // Empty Variable Declaration ("let x" or "create a variable called x")
      } else if (
        lower.startsWith("create a variable") ||
        lower.startsWith("let ")
      ) {
        const namePart = lower
          .replace(/^(?:create a (?:led )?variable(?: called)?|let)\s+/, "")
          .trim();
        const varName =
          namePart.split(" ")[0].replace(/[^a-zA-Z0-9]/g, "") || "myVar";
        codeToAdd = `\nlet ${varName};\n`;
        spokenResponse = `Created variable ${varName}.`;

        // Console Log ("print X to the console", "console log in bracket X", "write a console log")
      } else if (
        lower.includes("console log") ||
        lower.includes("console lock") ||
        lower.includes("print")
      ) {
        let logMatch = transcript.match(
          /(?:console log|console lock|print)\s*(?:in brackets|in bracket|in quotes|in quote)?\s*(.*?)(?:\s*to the console)?$/i,
        );

        let val = "";
        if (logMatch && logMatch[1]) {
          let content = logMatch[1].trim();
          content = content.replace(/to the console$/i, "").trim();

          if (content && content.toLowerCase() !== "") {
            if (!isNaN(content)) {
              val = content;
            } else if (
              content.toLowerCase() === "true" ||
              content.toLowerCase() === "false"
            ) {
              val = content.toLowerCase();
            } else {
              val = `"${content}"`;
            }
          }
        }
        codeToAdd = `\nconsole.log(${val});\n`;
        spokenResponse = val
          ? `Added a console log.`
          : `Added an empty console log.`;

        // Loops
      } else if (
        lower.startsWith("create a loop") ||
        lower.includes("write a loop")
      ) {
        codeToAdd = `\nfor (let i = 0; i < 5; i++) {\n  \n}\n`;
        spokenResponse = `Generated a for loop over 5 iterations.`;

        // Commands
      } else if (lower === "run code" || lower === "play code sonically") {
        onRun();
        spokenResponse = `Running code.`;
      } else if (lower === "clear code") {
        handleClearCode();
        return;
      } else {
        // Fallback: Just insert as a comment
        codeToAdd = `\n// ${transcript}\n`;
        spokenResponse = `Not recognized. Added as a comment.`;
      }

      // Retrieve latest value from ref to avoid stale closures and functional update issues
      const currentValue =
        typeof latestValueRef.current === "string"
          ? latestValueRef.current
          : "";
      onChange(currentValue + codeToAdd);
      speakText(spokenResponse);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onChange, speakText, onRun],
  );

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert(
        "Your browser does not support Voice Recognition. Try Chrome or Edge.",
      );
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
    } else {
      recognitionRef.current.start();
    }
  };

  // AI Mental Model Translator logic (AST parsing)
  const handleExplainLogic = useCallback(() => {
    if (!value || !value.trim()) {
      speakText(
        "Your editor is currently empty. Write some code first so I can explain it.",
      );
      return;
    }

    setIsExplaining(true);
    speakText("Analyzing code logic...");
    announceToScreenReader("AI is analyzing your code logic.");

    setTimeout(() => {
      let explanation = "";

      try {
        // Parse actual user code into an Abstract Syntax Tree
        const ast = acorn.parse(value, { ecmaVersion: 2020 });

        ast.body.forEach((node) => {
          if (node.type === "VariableDeclaration") {
            node.declarations.forEach((decl) => {
              const varName = decl.id.name;
              explanation += `You created a variable named ${varName}. `;
            });
          } else if (node.type === "FunctionDeclaration") {
            explanation += `You declared a function called ${node.id.name} `;
            if (node.params.length > 0) {
              explanation += `that takes the parameters: ${node.params.map((p) => p.name).join(", ")}. `;
            } else {
              explanation += `that takes no parameters. `;
            }
          } else if (node.type === "IfStatement") {
            explanation += `You created an IF statement condition. `;
            if (node.alternate) {
              explanation += `It also has an ELSE fallback. `;
            }
          } else if (node.type === "ForStatement") {
            explanation += `You wrote a FOR loop to repeat actions. `;
          } else if (node.type === "WhileStatement") {
            explanation += `You wrote a WHILE loop. `;
          } else if (node.type === "ExpressionStatement") {
            const expr = node.expression;
            if (expr.type === "CallExpression") {
              if (expr.callee.name) {
                explanation += `You are calling the function ${expr.callee.name}. `;
              } else if (expr.callee.object?.name === "console") {
                explanation += `You are logging output to the console. `;
              } else {
                explanation += `You are invoking a function. `;
              }
            } else if (expr.type === "AssignmentExpression") {
              if (expr.left.name) {
                explanation += `You reassigned the value of ${expr.left.name}. `;
              }
            }
          }
        });

        if (explanation === "") {
          explanation = `Your code is syntactically valid, but I don't see any major structural logic like variables or functions yet. `;
        }

        // Analyze what is MISSING based on the question goal
        let missingFeedback = "";
        if (question || description) {
          const goalLower = (
            (question || "") +
            " " +
            (description || "")
          ).toLowerCase();
          const codeLower = value.toLowerCase();

          if (
            (goalLower.includes("function") || goalLower.includes("method")) &&
            !codeLower.includes("function")
          ) {
            missingFeedback += "create a function, ";
          }
          if (
            (goalLower.includes("loop") || goalLower.includes("iterate")) &&
            !codeLower.includes("for") &&
            !codeLower.includes("while")
          ) {
            missingFeedback += "add a loop, ";
          }
          if (
            goalLower.includes("variable") &&
            !codeLower.includes("let ") &&
            !codeLower.includes("const ") &&
            !codeLower.includes("var ")
          ) {
            missingFeedback += "declare a variable, ";
          }
          if (goalLower.includes(" if ") && !codeLower.includes("if")) {
            missingFeedback += "use an if statement, ";
          }
          if (goalLower.includes("return") && !codeLower.includes("return")) {
            missingFeedback += "return a final value, ";
          }
          if (
            (goalLower.includes("print") || goalLower.includes("console")) &&
            !codeLower.includes("console.log")
          ) {
            missingFeedback += "print output to the console, ";
          }
        }

        if (missingFeedback !== "") {
          // removing the trailing comma and space
          missingFeedback = missingFeedback.slice(0, -2);
          explanation += ` However, based on the goal, it looks like you still need to ${missingFeedback}. `;
        } else {
          explanation += ` You seem to have the core requirements for the assignment. `;
        }

        // Report on the actual OUTPUT or ERRORS
        if (error) {
          explanation += ` I also noticed your last run produced an error: ${error}. Let's fix that first. `;
        } else if (output) {
          explanation += ` Your code successfully outputs: ${output}. Does this match what the question asked for? `;
        }
      } catch (err) {
        // If it fails to parse, it means there's a syntax error
        explanation = `I tried to read the code, but there is a syntax error at line ${err.loc?.line || "unknown"}. ${err.message.replace(/\s*\(\d+:\d+\)$/, "")}. Please check your spelling and brackets before running.`;
      }

      setIsExplaining(false);
      speakText(explanation);
      announceToScreenReader("Explanation ready: " + explanation);
    }, 1500); // 1.5s delay to simulate the "AI" thinking phase
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, speakText, question, description, output, error]);

  const handleKeyDown = (e) => {
    // Intentionally NOT trapping the "Tab" key to comply with WCAG 2.1.2 (No Keyboard Trap).
    // Blind users can navigate away from the Textarea using Tab/Shift+Tab naturally.

    // Ctrl/Cmd + Enter to run code
    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
      e.preventDefault();
      onRun();
      announceToScreenReader("Running your code");
    }

    // Ctrl/Cmd + M to start Voice Dictation
    if ((e.ctrlKey || e.metaKey) && e.key === "m") {
      e.preventDefault();
      toggleListening();
    }

    // Ctrl/Cmd + E for AI Explanation
    if ((e.ctrlKey || e.metaKey) && e.key === "e") {
      e.preventDefault();
      if (!isExplaining) {
        handleExplainLogic();
      }
    }

    // Ctrl/Cmd + L to announce current line number
    if ((e.ctrlKey || e.metaKey) && e.key === "l") {
      e.preventDefault();
      const lineNum = getCurrentLineNumber();
      announceToScreenReader(`Line ${lineNum} of ${lineCount}`);
      speakText(`Line ${lineNum}`);
    }

    // Ctrl/Cmd + H to hear hint
    if ((e.ctrlKey || e.metaKey) && e.key === "h") {
      e.preventDefault();
      if (hint) {
        announceToScreenReader(`Hint: ${hint}`);
        speakText(hint);
      }
    }
  };

  const getCurrentLineNumber = () => {
    const textarea = textareaRef.current;
    if (!textarea) return 1;
    const cursorPosition = textarea.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPosition);
    return textBeforeCursor.split("\n").length;
  };

  const handleCursorMove = () => {
    const lineNum = getCurrentLineNumber();
    if (lineNum !== currentLine) {
      setCurrentLine(lineNum);
    }
  };

  const announceToScreenReader = (message) => {
    const announcement = document.createElement("div");
    announcement.setAttribute("role", "status");
    announcement.setAttribute("aria-live", "polite");
    announcement.className = "sr-only";
    announcement.textContent = message;
    document.body.appendChild(announcement);

    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  };

  

  return (
    <VStack spacing={6} align="stretch" width="100%">
      <Box>
        <Heading size="lg" mb={3} id="coding-question-heading">
          {question}
        </Heading>
        <Text fontSize="md" color={textColor} mb={2}>
          {description}
        </Text>
        {hint && (
          <Text
            fontSize="sm"
            color="blue.500"
            fontStyle="italic"
            role="note"
            aria-label={`Hint: ${hint}`}
          >
            💡 Hint: {hint} (Press Ctrl+H to hear hint again)
          </Text>
        )}
      </Box>

      <Box
        p={3}
        bg={outputBg}
        borderRadius="md"
        borderWidth="1px"
        borderColor={borderColor}
        role="region"
        aria-label="Keyboard shortcuts"
        id="keyboard-shortcuts"
      >
        <Text fontSize="sm" fontWeight="bold" mb={2}>
          Keyboard Shortcuts:
        </Text>
        <VStack align="start" spacing={1} fontSize="xs">
          <Text>• Up/Down Arrow: Move between lines of code</Text>
          <Text>• Shift + Right/Left Arrow: Select code characters</Text>
          <Text>
            • Tab / Shift + Tab: Move focus to the next/previous element (leave
            editor)
          </Text>
          <Text>• Ctrl/Cmd + M: Start/Stop Voice Dictation 🎤</Text>
          <Text>• Ctrl/Cmd + E: AI Explain Code Logic 🧠</Text>
          <Text>• Ctrl/Cmd + Enter: Run code</Text>
          <Text>• Ctrl/Cmd + L: Announce current line number</Text>
          <Text>• Ctrl/Cmd + H: Hear hint</Text>
        </VStack>
      </Box>

      <Box
        borderWidth="2px"
        borderColor={
          isListening ? "blue.400" : isExplaining ? "purple.400" : borderColor
        }
        borderRadius="md"
        overflow="hidden"
        bg={cardBg}
        boxShadow={
          isListening
            ? "0 0 10px rgba(66, 153, 225, 0.6)"
            : isExplaining
              ? "0 0 10px rgba(159, 122, 234, 0.6)"
              : "none"
        }
        transition="all 0.2s"
      >
        <HStack
          bg={isListening ? "blue.50" : isExplaining ? "purple.50" : outputBg}
          px={4}
          py={2}
          borderBottomWidth="1px"
          borderColor={
            isListening ? "blue.200" : isExplaining ? "purple.200" : borderColor
          }
          justify="space-between"
        >
          <Text
            fontSize="sm"
            fontWeight="medium"
            color={
              isListening ? "blue.600" : isExplaining ? "purple.600" : "inherit"
            }
          >
            {isListening
              ? "🎙️ Listening..."
              : isExplaining
                ? "🧠 AI Analyzing Logic..."
                : `Code Editor (Line ${currentLine} of ${lineCount})`}
          </Text>
          <HStack spacing={2} wrap="wrap">
            <Button
              size="xs"
              colorScheme={isExplaining ? "purple" : "purple"}
              variant={isExplaining ? "solid" : "outline"}
              onClick={handleExplainLogic}
              aria-label="Explain Code Logic with AI"
              isLoading={isExplaining}
              loadingText="Analyzing..."
            >
              🧠 Explain Logic
            </Button>
            <Button
              size="xs"
              colorScheme={isListening ? "red" : "blue"}
              variant={isListening ? "solid" : "outline"}
              onClick={toggleListening}
              aria-label="Toggle voice dictation"
            >
              {isListening ? "Stop Mic" : "🎤 Dictate"}
            </Button>
            <Button size="xs" onClick={handleClearCode} variant="ghost">
              Clear
            </Button>
            <Button
              size="xs"
              onClick={onRun}
              colorScheme="green"
              aria-label="Run code"
            >
              Run
            </Button>
          </HStack>
        </HStack>

        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onKeyUp={handleCursorMove}
          onClick={handleCursorMove}
          placeholder="Type your code here, or press the Dictate button to speak..."
          fontFamily="monospace"
          fontSize="md"
          minHeight="300px"
          resize="vertical"
          border="none"
          _focus={{ outline: "none", boxShadow: "none" }}
          aria-label={
            ariaLabel || "Code editor. Type or dictate your code here."
          }
          aria-describedby="coding-question-heading keyboard-shortcuts"
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
        />
      </Box>

      {(output || error) && (
        <Box
          p={4}
          bg={outputBg}
          borderRadius="md"
          borderWidth="2px"
          borderColor={error ? errorColor : successColor}
          role="region"
          aria-live="polite"
          aria-label={error ? "Error output" : "Code output"}
        >
          <Text
            fontWeight="bold"
            mb={2}
            color={error ? errorColor : successColor}
          >
            {error ? "❌ Error:" : "✅ Output:"}
          </Text>
          <Box
            as="pre"
            fontFamily="monospace"
            fontSize="sm"
            whiteSpace="pre-wrap"
            color={textColor}
          >
            {error || output}
          </Box>
        </Box>
      )}
    </VStack>
  );
};
